import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { BarChart3, Eye, TrendingUp, Car } from 'lucide-react';

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-white">Loading analytics...</div>;
  }

  const topVehicles = analytics?.vehicle_views?.slice(0, 10) || [];
  const pageViews = analytics?.page_views || {};

  return (
    <div data-testid="analytics-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <p className="text-[#a8b8d0] text-sm mt-1">
          Track vehicle page views and site traffic
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#1a4d8c] rounded-lg">
              <Eye size={20} className="text-white" />
            </div>
            <span className="text-[#a8b8d0]">Vehicle Views</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {pageViews.vehicle || 0}
          </p>
        </div>
        
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#ff6b35] rounded-lg">
              <BarChart3 size={20} className="text-white" />
            </div>
            <span className="text-[#a8b8d0]">Page Views</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {Object.values(pageViews).reduce((a, b) => a + b, 0)}
          </p>
        </div>
        
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#22c55e] rounded-lg">
              <Car size={20} className="text-white" />
            </div>
            <span className="text-[#a8b8d0]">Tracked Vehicles</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {topVehicles.length}
          </p>
        </div>
        
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#ffc107] rounded-lg">
              <TrendingUp size={20} className="text-white" />
            </div>
            <span className="text-[#a8b8d0]">Avg Views/Vehicle</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {topVehicles.length > 0 
              ? Math.round(topVehicles.reduce((sum, v) => sum + (v.view_count || 0), 0) / topVehicles.length)
              : 0}
          </p>
        </div>
      </div>

      {/* Top Vehicles Table */}
      <div className="bg-[#132238] rounded-xl border border-[#1a2d47] overflow-hidden">
        <div className="p-4 border-b border-[#1a2d47]">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <TrendingUp size={20} className="text-[#ff6b35]" />
            Most Viewed Vehicles
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a2d47]">
              <tr>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">#</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Vehicle</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Views</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Popularity</th>
              </tr>
            </thead>
            <tbody>
              {topVehicles.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-[#a8b8d0]">
                    No vehicle views tracked yet
                  </td>
                </tr>
              ) : (
                topVehicles.map((vehicle, index) => {
                  const maxViews = topVehicles[0]?.view_count || 1;
                  const percentage = Math.round((vehicle.view_count / maxViews) * 100);
                  
                  return (
                    <tr key={vehicle.id} className="border-t border-[#1a2d47]">
                      <td className="px-4 py-3 text-[#a8b8d0]">{index + 1}</td>
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-white font-medium">
                          {vehicle.view_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 w-1/3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-[#1a2d47] rounded-full h-2">
                            <div 
                              className="bg-[#ff6b35] h-2 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-[#a8b8d0] text-sm w-12">
                            {percentage}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Page Views by Type */}
      {Object.keys(pageViews).length > 0 && (
        <div className="mt-6 bg-[#132238] rounded-xl border border-[#1a2d47] p-6">
          <h2 className="text-lg font-bold text-white mb-4">Page Views by Type</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(pageViews).map(([type, count]) => (
              <div key={type} className="bg-[#1a2d47]/50 rounded-lg p-4">
                <p className="text-[#a8b8d0] text-sm capitalize">{type}</p>
                <p className="text-2xl font-bold text-white">{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
